import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import { apiPost } from '../../lib/api';
import { inr2 } from '../../lib/format';
import { ChargesTable, cleanCharges } from './ChargesTable';

const qline = (q, lid) => (q.line_items || []).find(l => l.id === lid);

/** #8 — award each RFQ line to a vendor (default: lowest quote), charges per vendor PO. */
export function ItemwiseAwardModal({ rfq, onClose, onDone }) {
  const quotes = (rfq.quotes || []).filter(q => !q.withdrawn && q.vendor_id);
  const awarded = new Set(rfq.awarded_line_ids || []);
  const lines = (rfq.line_items || []).filter(l => !awarded.has(l.id));
  const lowest = (lid) => {
    const c = quotes.filter(q => qline(q, lid)?.rate > 0).sort((a, b) => qline(a, lid).rate - qline(b, lid).rate);
    return c[0]?.vendor_id || '';
  };
  const [pick, setPick] = useState(Object.fromEntries(lines.map(l => [l.id, lowest(l.id)])));
  const [charges, setCharges] = useState({});
  const [busy, setBusy] = useState(false);
  const byVendor = useMemo(() => {
    const out = {};
    lines.forEach(l => {
      const vid = pick[l.id]; if (!vid) return;
      const q = quotes.find(x => x.vendor_id === vid); const ql = qline(q, l.id) || {};
      out[vid] = out[vid] || { name: q?.vendor_name || q?.vendor, total: 0, items: [] };
      out[vid].total += Number(ql.qty ?? l.qty ?? 0) * Number(ql.rate || 0);
      out[vid].items.push({ gst_rate: ql.gst_rate ?? l.gst_rate ?? 18 });
    });
    return out;
  }, [pick, lines, quotes]);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await apiPost(`/rfq/${rfq.id}/award-per-line`, {
        lines: lines.filter(l => pick[l.id]).map(l => ({ line_id: l.id, vendor_id: pick[l.id] })),
        charges_by_vendor: Object.fromEntries(Object.entries(charges).map(([k, v]) => [k, cleanCharges(v)])),
      });
      toast.success(`${r.pos_created} PO(s) created: ${r.po_nos.join(', ')} · RFQ ${r.rfq_status}`);
      onDone?.();
    } catch (e) { const d = e?.response?.data?.detail; toast.error(typeof d === 'string' ? d : (d?.message || 'Award failed')); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" data-testid="itemwise-award-modal">
      <div className="bg-white rounded-sm w-full max-w-4xl max-h-[92vh] overflow-y-auto p-5 space-y-4">
        <div className="flex justify-between"><h3 className="font-display font-bold text-[#3b4872]">Award item-wise · {rfq.rfq_no}</h3><button onClick={onClose} data-testid="itemwise-close"><X size={16} /></button></div>
        <table className="w-full text-xs border border-slate-200">
          <thead className="bg-slate-50 text-slate-500"><tr><th className="px-2 py-1 text-left">Item</th><th className="px-2 py-1 text-left">MPR / Project</th><th className="px-2 py-1 text-right">Qty</th><th className="px-2 py-1 text-left">Vendor (rate)</th></tr></thead>
          <tbody>{lines.map((l, i) => (
            <tr key={l.id} className="border-t border-slate-100">
              <td className="px-2 py-1">{l.material_name}</td><td className="px-2 py-1 text-slate-500">{l.mpr_no || '—'} · {l.project_name || ''}</td>
              <td className="px-2 py-1 text-right">{l.qty} {l.uom}</td>
              <td className="px-2 py-1"><select value={pick[l.id] || ''} onChange={e => setPick({ ...pick, [l.id]: e.target.value })} className="px-1.5 py-1 border border-slate-300 rounded-sm bg-white" data-testid={`itemwise-vendor-${i}`}>
                <option value="">— leave open —</option>
                {quotes.filter(q => qline(q, l.id)).map(q => <option key={q.vendor_id} value={q.vendor_id}>{q.vendor_name || q.vendor} ({inr2(qline(q, l.id).rate)})</option>)}
              </select></td>
            </tr>))}</tbody>
        </table>
        {Object.entries(byVendor).map(([vid, v]) => (
          <div key={vid} className="border border-slate-200 rounded-sm p-3 space-y-2" data-testid={`itemwise-vendor-block-${vid}`}>
            <div className="flex justify-between text-sm"><b>{v.name}</b><span>{v.items.length} line(s) · {inr2(v.total)} before GST</span></div>
            <ChargesTable charges={charges[vid] || []} onChange={c => setCharges({ ...charges, [vid]: c })} items={v.items} testid={`itemwise-charges-${vid.slice(0, 6)}`} />
          </div>))}
        <div className="flex justify-end gap-2"><button onClick={onClose} className="px-3 py-1.5 text-sm">Cancel</button>
          <button onClick={submit} disabled={busy || !Object.keys(byVendor).length} className="px-4 py-2 bg-[#3b4872] text-white text-sm rounded-sm font-semibold disabled:opacity-50" data-testid="itemwise-create-pos">Create POs</button></div>
      </div>
    </div>
  );
}
